import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  DatePicker,
  Select,
  Row,
  Col,
  Typography,
  Spin,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import type { ReportParams } from '@/services/reportDocuments.service';
import {
  getLedger,
  type LedgerReport,
  type LedgerLine,
  type LedgerType,
  type GetLedgerParams,
} from '@/services/financialReports.service';
import api from '@/services/api.service';
import customerService from '@/services/customer.service';
import { formatCustomerOptionLabel } from '@/utils/customerOptionLabel';
import { projectService } from '@/services/project.service';
import { formatLedgerBalance, getLedgerReferenceLabel, getVoucherLink, type LedgerPageLocationState } from '@/utils/financeVoucherLinks.util';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const LEDGER_TYPE_VALUES: LedgerType[] = ['account', 'supplier', 'customer', 'bank', 'cash', 'project'];
const LEDGER_TYPE_LABEL_KEYS: Record<LedgerType, string> = {
  account: 'ledgerTypeAccount',
  supplier: 'ledgerTypeSupplier',
  customer: 'ledgerTypeCustomer',
  bank: 'ledgerTypeBank',
  cash: 'ledgerTypeCash',
  project: 'ledgerTypeProject',
};

const ENTITY_PARAM: Partial<Record<LedgerType, keyof GetLedgerParams>> = {
  account: 'account_id',
  supplier: 'supplier_id',
  customer: 'customer_id',
  bank: 'bank_account_id',
  project: 'project_id',
};

/** Report document params for the standard toolbar (PDF / Excel / email) built from the last viewed ledger. */
function toDocParams(p: GetLedgerParams): ReportParams {
  const out: ReportParams = { ledger_type: p.ledger_type, from: p.from_date, to: p.to_date };
  const key = ENTITY_PARAM[p.ledger_type];
  if (key && p[key]) out[key] = String(p[key]);
  return out;
}

export const LedgerPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const prefilledFromNav = useRef(false);
  const ledgerTypeOptions = useMemo(
    () => LEDGER_TYPE_VALUES.map((value) => ({ value, label: t(LEDGER_TYPE_LABEL_KEYS[value]) })),
    [t]
  );
  const [viewed, setViewed] = useState<GetLedgerParams | null>(null);
  const [ledgerType, setLedgerType] = useState<LedgerType>('account');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs(),
  ]);
  const [accountId, setAccountId] = useState<string | undefined>();
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [bankAccountId, setBankAccountId] = useState<string | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [report, setReport] = useState<LedgerReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fromDate = dateRange[0].format('YYYY-MM-DD');
  const toDate = dateRange[1].format('YYYY-MM-DD');

  const { data: coaList = [] } = useQuery({
    queryKey: ['ledger-chart-of-accounts'],
    queryFn: async () => {
      const res = await api.get<any>('/finance/chart-of-accounts');
      const data = (res as any)?.data ?? res;
      return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
    },
  });

  const { data: suppliersList = [] } = useQuery({
    queryKey: ['ledger-suppliers'],
    queryFn: async () => {
      const res = await api.get<any>('/procurement/suppliers');
      const data = (res as any)?.data ?? res;
      return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
    },
  });

  const { data: customersList = [] } = useQuery({
    queryKey: ['ledger-customers'],
    queryFn: () => customerService.getActiveCustomers(),
  });

  const { data: bankAccountsList = [] } = useQuery({
    queryKey: ['ledger-bank-accounts'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: { id: string; account_name: string }[] }>(
        '/finance/reports/ledger/bank-accounts'
      );
      const data = (res as any)?.data ?? res;
      return data?.data ?? [];
    },
  });

  const { data: projectsList = [] } = useQuery({
    queryKey: ['ledger-projects'],
    queryFn: () => projectService.getProjectsForSelection(),
  });

  const accountOptions = useMemo(
    () =>
      (coaList as any[]).map((a: any) => ({
        value: a.id,
        label: `${a.account_code ?? ''} - ${a.account_name ?? a.id}`,
      })),
    [coaList]
  );
  const supplierOptions = useMemo(
    () =>
      (suppliersList as any[]).map((s: any) => ({
        value: s.id,
        label: s.supplier_name ?? s.supplier_code ?? s.id,
      })),
    [suppliersList]
  );
  const customerOptions = useMemo(
    () =>
      (customersList as any[]).map((c: any) => ({
        value: c.id,
        label: formatCustomerOptionLabel(c),
      })),
    [customersList]
  );
  const bankOptions = useMemo(
    () =>
      bankAccountsList.map((b: { id: string; account_name: string }) => ({
        value: b.id,
        label: b.account_name,
      })),
    [bankAccountsList]
  );
  const projectOptions = useMemo(
    () =>
      (projectsList as any[]).map((p: any) => ({
        value: p.id,
        label: p.project_name ?? p.project_number ?? p.id,
      })),
    [projectsList]
  );

  const entityId =
    ledgerType === 'account'
      ? accountId
      : ledgerType === 'supplier'
        ? supplierId
        : ledgerType === 'customer'
          ? customerId
          : ledgerType === 'bank'
            ? bankAccountId
            : ledgerType === 'project'
              ? projectId
              : undefined;

  const canView =
    ledgerType === 'cash' || (entityId && fromDate && toDate);

  const load = (params: GetLedgerParams) => {
    setLoading(true);
    setError(null);
    setViewed(params);
    getLedger(params)
      .then(setReport)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('failedLoadLedger')))
      .finally(() => setLoading(false));
  };

  const handleView = () => {
    if (!canView) return;
    const params: GetLedgerParams = {
      ledger_type: ledgerType,
      from_date: fromDate,
      to_date: toDate,
    };
    if (ledgerType === 'account') params.account_id = accountId;
    else if (ledgerType === 'supplier') params.supplier_id = supplierId;
    else if (ledgerType === 'customer') params.customer_id = customerId;
    else if (ledgerType === 'bank') params.bank_account_id = bankAccountId;
    else if (ledgerType === 'project') params.project_id = projectId;
    load(params);
  };

  /** Prefill from navigation state (drill-down) or URL params (`?ledger_type=account&account_id=..&from=..&to=..`). */
  useEffect(() => {
    if (prefilledFromNav.current) return;
    const state = location.state as LedgerPageLocationState | null;
    const urlType = searchParams.get('ledger_type') as LedgerType | null;
    const lt: LedgerType | null = state?.accountId ? state.ledgerType ?? 'account' : urlType && LEDGER_TYPE_VALUES.includes(urlType) ? urlType : null;
    if (!lt) return;
    const from = state?.fromDate ?? searchParams.get('from') ?? dayjs(searchParams.get('to') ?? undefined).startOf('year').format('YYYY-MM-DD');
    const to = state?.toDate ?? searchParams.get('to') ?? dayjs().format('YYYY-MM-DD');
    const params: GetLedgerParams = { ledger_type: lt, from_date: from, to_date: to };
    const key = ENTITY_PARAM[lt];
    const id = state?.accountId ?? (key ? searchParams.get(key) : null);
    if (key) {
      if (!id) return;
      (params as unknown as Record<string, string>)[key] = id;
    }

    prefilledFromNav.current = true;
    setLedgerType(lt);
    if (lt === 'account') setAccountId(id ?? undefined);
    else if (lt === 'supplier') setSupplierId(id ?? undefined);
    else if (lt === 'customer') setCustomerId(id ?? undefined);
    else if (lt === 'bank') setBankAccountId(id ?? undefined);
    else if (lt === 'project') setProjectId(id ?? undefined);
    setDateRange([dayjs(from), dayjs(to)]);
    load(params);

    if (state) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state, navigate]);

  const tableColumns: ColumnsType<LedgerLine> = [
    {
      title: t('date'),
      dataIndex: 'voucher_date',
      key: 'voucher_date',
      fixed: 'left',
      width: 110,
    },
    {
      title: t('voucherNo'),
      dataIndex: 'voucher_number',
      key: 'voucher_number',
      width: 130,
    },
    {
      title: t('type'),
      dataIndex: 'voucher_type',
      key: 'voucher_type',
      width: 100,
    },
    {
      title: t('reference'),
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 150,
      render: (_text, record) => (
        <a
          href={getVoucherLink(record)}
          onClick={(e) => {
            e.preventDefault();
            navigate(getVoucherLink(record));
          }}
        >
          {getLedgerReferenceLabel(record)}
        </a>
      ),
    },
    {
      title: t('narration'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('debit'),
      dataIndex: 'debit_amount',
      key: 'debit_amount',
      width: 120,
      align: 'right',
      render: (v) => (v > 0 ? formatCurrency(v) : '-'),
    },
    {
      title: t('credit'),
      dataIndex: 'credit_amount',
      key: 'credit_amount',
      width: 120,
      align: 'right',
      render: (v) => (v > 0 ? formatCurrency(v) : '-'),
    },
    {
      title: t('runningBalance'),
      dataIndex: 'running_balance',
      key: 'running_balance',
      width: 130,
      align: 'right',
      render: (v) => (
        <Text strong>{formatLedgerBalance(v, formatCurrency)}</Text>
      ),
    },
  ];

  const showEntitySelector =
    ledgerType !== 'cash';
  const entitySelectorLabel =
    ledgerType === 'account'
      ? t('account')
      : ledgerType === 'supplier'
        ? t('supplier')
        : ledgerType === 'customer'
          ? t('customer')
          : ledgerType === 'bank'
            ? t('bankAccount')
            : ledgerType === 'project'
              ? t('project')
              : '';

  return (
    <div className="ledger-page" style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 4 }}>{t('ledger')}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('ledgerHint')}
      </Text>
      <Alert
        type="info"
        showIcon
        className="no-print"
        style={{ marginBottom: 16, maxWidth: 900 }}
        message={t('ledgerRegister')}
        description={t('ledgerRegisterDesc')}
      />
      <Card className="no-print">
        <Space wrap size="middle" align="start">
          <Space direction="vertical" size={4}>
            <Text type="secondary">{t('ledgerType')}</Text>
            <Select
              value={ledgerType}
              onChange={(v) => {
                setLedgerType(v);
                setAccountId(undefined);
                setSupplierId(undefined);
                setCustomerId(undefined);
                setBankAccountId(undefined);
                setProjectId(undefined);
              }}
              options={ledgerTypeOptions}
              style={{ width: 220 }}
              showSearch
              optionFilterProp="label"
            />
          </Space>
          {showEntitySelector && (
            <Space direction="vertical" size={4}>
              <Text type="secondary">{entitySelectorLabel}</Text>
              {ledgerType === 'account' && (
                <Select
                  placeholder={t('selectAccount')}
                  value={accountId}
                  onChange={setAccountId}
                  options={accountOptions}
                  style={{ width: 280 }}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
              {ledgerType === 'supplier' && (
                <Select
                  placeholder={t('selectSupplier')}
                  value={supplierId}
                  onChange={setSupplierId}
                  options={supplierOptions}
                  style={{ width: 280 }}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
              {ledgerType === 'customer' && (
                <Select
                  placeholder={t('selectCustomer')}
                  value={customerId}
                  onChange={setCustomerId}
                  options={customerOptions}
                  style={{ width: 280 }}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
              {ledgerType === 'bank' && (
                <Select
                  placeholder={t('selectBankAccount')}
                  value={bankAccountId}
                  onChange={setBankAccountId}
                  options={bankOptions}
                  style={{ width: 280 }}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
              {ledgerType === 'project' && (
                <Select
                  placeholder={t('selectProject')}
                  value={projectId}
                  onChange={setProjectId}
                  options={projectOptions}
                  style={{ width: 280 }}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            </Space>
          )}
          {ledgerType === 'cash' && (
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('cash')}</Text>
              <Text>{t('allCashAccounts')}</Text>
            </Space>
          )}
          <Space direction="vertical" size={4}>
            <Text type="secondary">{t('dateRange')}</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            />
          </Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleView}
            disabled={!canView}
            style={{ marginTop: 24 }}
          >
            {tc('view')}
          </Button>
        </Space>
        {error && (
          <Alert type="error" message={error} style={{ marginTop: 16 }} closable onClose={() => setError(null)} />
        )}
      </Card>

      {report && (
        <Card style={{ marginTop: 16 }} className="ledger-report-card">
          <div className="no-print" style={{ marginBottom: 16 }}>
            <ReportDocumentActions reportKey="general-ledger" params={viewed ? toDocParams(viewed) : null} />
          </div>
          <div>
            <div style={{ marginBottom: 12 }}>
              <Title level={5} style={{ marginBottom: 4 }}>{t('ledgerReport')}</Title>
              <Text>
                {t(LEDGER_TYPE_LABEL_KEYS[ledgerType])} • {t('periodFromTo', { from: fromDate, to: toDate })} • {report.currency}
              </Text>
            </div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Text strong>{t('openingBalanceColon')} </Text>
                <Text>{formatLedgerBalance(report.opening_balance, formatCurrency)}</Text>
              </Col>
            </Row>
            {loading ? (
              <Spin tip={tc('loading')} />
            ) : (
              <Table
                size="small"
                columns={tableColumns}
                dataSource={report.lines.map((l, i) => ({ ...l, key: l.voucher_id + '-' + i }))}
                sticky={{ offsetHeader: 0 }}
                scroll={{ x: 1100, y: 480 }}
                pagination={{
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  showTotal: (count) => t('totalLines', { count }),
                  pageSizeOptions: ['10', '20', '50', '100'],
                  hideOnSinglePage: report.lines.length <= 10,
                }}
              />
            )}
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col>
                <Text strong>{t('totalDebitColon')} </Text>
                <Text>{formatCurrency(report.total_debit)}</Text>
              </Col>
              <Col>
                <Text strong>{t('totalCreditColon')} </Text>
                <Text>{formatCurrency(report.total_credit)}</Text>
              </Col>
              <Col>
                <Text strong>{t('closingBalanceColon')} </Text>
                <Text>{formatLedgerBalance(report.closing_balance, formatCurrency)}</Text>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .ledger-page { padding: 0; }
        }
      `}</style>
    </div>
  );
};

export default LedgerPage;
